import { NormalizedNumber } from '@sparkdotfi/common-universal'

import {
  AccountMetadata,
  AccountSparkPointsSummary,
  AccountSparkRewardsSummary,
  InterestData,
  MigrationInfo,
  SavingsAccountSupportedStablecoin,
  StusdsWarningData,
} from '../../types'
import { AccountMainPanelGroup } from '../account-main-panel-group/AccountMainPanelGroup'
import { DepositCTAPanel } from '../deposit-cta-panel/DepositCTAPanel'
import { EntryAssetsPanel } from '../entry-assets-panel/EntryAssetsPanel'
import { LegacyAccountBanner } from '../legacy-account-banner/LegacyAccountBanner'
import { SavingsCharts } from '../savings-charts/SavingsCharts'
import { StusdsWarning } from '../stusds-warning/StusdsWarning'
import { UpgradeSavingsBanner } from '../upgrade-savings-banner/UpgradeSavingsBanner'
import { BaseMiniAppOptions } from '@/base-miniapp/types'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { UseSavingsChartsResult } from '@/features/savings/logic/useSavingsCharts'

export interface SavingsAccountProps {
  savingsToken: Token
  savingsTokenBalance: NormalizedNumber
  underlyingToken: Token
  interestData: InterestData
  supportedAssets: SavingsAccountSupportedStablecoin[]
  mostValuableAsset: Token
  showConvertDialogButton: boolean
  charts: UseSavingsChartsResult
  openDepositDialog: (tokenToDeposit: Token) => void
  openSendDialog: () => void
  openWithdrawDialog: () => void
  openConvertStablesDialog: () => void
  openSandboxModal: () => void
  openConnectModal: () => void
  guestMode: boolean
  isInSandbox: boolean
  hideSendDialogButton: boolean
  migrationInfo?: MigrationInfo
  metadata: AccountMetadata
  sparkRewardsSummary: AccountSparkRewardsSummary
  sparkPointsSummary: AccountSparkPointsSummary
  stusdsWarningData: StusdsWarningData | undefined
  baseMiniAppOptions: BaseMiniAppOptions | undefined
}

export function SavingsAccount({
  savingsToken,
  savingsTokenBalance,
  underlyingToken,
  interestData,
  supportedAssets,
  mostValuableAsset,
  showConvertDialogButton,
  charts,
  openDepositDialog,
  openSendDialog,
  openWithdrawDialog,
  openConvertStablesDialog,
  openSandboxModal,
  openConnectModal,
  guestMode,
  isInSandbox,
  hideSendDialogButton,
  migrationInfo,
  metadata,
  sparkRewardsSummary,
  sparkPointsSummary,
  stusdsWarningData,
  baseMiniAppOptions,
}: SavingsAccountProps) {
  const displayDepositCallToAction = guestMode || savingsTokenBalance.eq(0)
  const displayUpgradeBanner = migrationInfo !== undefined && savingsTokenBalance.gt(0)
  const displayLegacyAccountBanner = migrationInfo !== undefined && savingsTokenBalance.eq(0)
  const primaryAction = guestMode
    ? { title: 'Connect Wallet' as const, action: openConnectModal }
    : {
        title: 'Deposit' as const,
        action: () => openDepositDialog(mostValuableAsset),
      }

  const skipSecondaryAction = isInSandbox || import.meta.env.VITE_BASE_MINI_APP === '1'

  return (
    <div className="grid grid-cols-1 gap-6">
      {displayDepositCallToAction ? (
        <DepositCTAPanel
          savingsRate={interestData.APY}
          apyExplainer={metadata.apyExplainer}
          apyExplainerDocsLink={metadata.apyExplainerDocsLink}
          entryTokens={supportedAssets.map((asset) => asset.token)}
          savingsToken={savingsToken}
          skipSecondaryAction={skipSecondaryAction}
          description={{
            text: metadata.description,
            docsLink: metadata.descriptionDocsLink,
          }}
          actions={{
            primary: primaryAction,
            secondary: { title: 'Try in Sandbox', action: openSandboxModal },
          }}
          sparkRewardsSummary={sparkRewardsSummary}
          sparkPointsSummary={sparkPointsSummary}
          className="min-h-[320px]"
          baseMiniAppOptions={baseMiniAppOptions}
        />
      ) : (
        <AccountMainPanelGroup
          underlyingToken={underlyingToken}
          savingsToken={savingsToken}
          savingsTokenBalance={savingsTokenBalance}
          calculateUnderlyingTokenBalance={interestData.calculateUnderlyingTokenBalance}
          balanceRefreshIntervalInMs={interestData.balanceRefreshIntervalInMs}
          openDepositDialog={() => openDepositDialog(mostValuableAsset as Token)}
          openSendDialog={openSendDialog}
          openWithdrawDialog={openWithdrawDialog}
          oneYearProjection={interestData.oneYearProjection}
          sparkRewardsOneYearProjection={interestData.sparkRewardsOneYearProjection}
          apy={interestData.APY}
          apyExplainer={metadata.apyExplainer}
          apyExplainerDocsLink={metadata.apyExplainerDocsLink}
          sparkRewardsSummary={sparkRewardsSummary}
          hideSendDialogButton={hideSendDialogButton}
          className="min-h-[320px]"
        />
      )}
      {stusdsWarningData && savingsToken.symbol === TokenSymbol('stUSDS') && (
        <StusdsWarning
          underlyingToken={underlyingToken}
          lockstakeDebt={stusdsWarningData.lockstakeDebt}
          totalAssets={stusdsWarningData.totalAssets}
        />
      )}

      {displayLegacyAccountBanner && migrationInfo && <LegacyAccountBanner {...migrationInfo} />}
      {displayUpgradeBanner && migrationInfo && <UpgradeSavingsBanner {...migrationInfo} />}

      <div ref={charts.accountChartsProps.chartsContainerRef} className="relative">
        {charts.accountChartsProps.highlightKey > 0 && (
          <div
            key={charts.accountChartsProps.highlightKey}
            className="pointer-events-none absolute inset-0 z-10 animate-highlight-flash rounded-sm"
            onAnimationEnd={charts.accountChartsProps.onHighlightEnd}
          />
        )}
        <SavingsCharts
          savingsToken={savingsToken}
          underlyingToken={underlyingToken}
          chartDataById={charts.chartDataById}
          isChartEnabledById={charts.isChartEnabledById}
          selectedTabId={charts.accountChartsProps.selectedChartTabId}
          onSelectedTabIdChange={charts.accountChartsProps.onSelectedChartTabIdChange}
        />
      </div>
      <EntryAssetsPanel
        assets={supportedAssets}
        openDepositDialog={openDepositDialog}
        openConvertStablesDialog={openConvertStablesDialog}
        showConvertDialogButton={showConvertDialogButton}
        guestMode={guestMode}
      />
    </div>
  )
}
